import React, { useState, useEffect, useRef } from 'react';
import { useServerStore } from '../../../store/serverStore';
import { Pencil, Check, X, Loader2, Camera, Upload } from 'lucide-react';
import { uploadAPI } from '../../../lib/api';

const OverviewTab: React.FC<{ serverId: string }> = ({ serverId }) => {
  const { currentServer, updateServer, fetchUserServers } = useServerStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const iconFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (currentServer) {
      setName(currentServer.name || '');
      setDescription(currentServer.description || '');
      setIconUrl(currentServer.icon_url || '');
    }
  }, [currentServer]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      if (!currentServer) throw new Error('Server not found');
      await updateServer(serverId, { name, description, icon_url: iconUrl });
      setSuccess('Server updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleIconFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIcon(true);
    setError(null);
    try {
      const { url } = await uploadAPI.uploadServerIcon(serverId, file);
      setIconUrl(url);
      await fetchUserServers();
      setSuccess('Icon updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingIcon(false);
      if (iconFileRef.current) iconFileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Overview</h2>
        <p className="text-xs text-text-muted mt-0.5">Manage your server's general information and appearance.</p>
      </div>

      {/* Server card — banner & icon preview */}
      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl overflow-hidden">
        {/* Banner area — click icon to upload */}
        <input ref={iconFileRef} type="file" accept="image/*" className="hidden" onChange={handleIconFile} />
        <div className="relative h-28 group cursor-pointer" onClick={() => iconFileRef.current?.click()}>
          {iconUrl ? (
            <div className="w-full h-full bg-gradient-to-r from-accent-color/20 via-accent-color/5 to-transparent flex items-center justify-center">
              <img
                src={iconUrl}
                alt=""
                className="h-16 w-16 rounded-2xl object-cover border-2 border-border-color shadow-lg"
              />
            </div>
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-accent-color/30 via-accent-color/10 to-transparent flex items-center justify-center">
              <div className="h-16 w-16 rounded-2xl bg-bg-tertiary border-2 border-border-color flex items-center justify-center text-2xl font-black text-text-muted shadow-lg">
                {currentServer?.name?.charAt(0).toUpperCase()}
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
            {uploadingIcon ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-white text-xs font-medium">
                <Camera className="w-4 h-4" />
                Change Icon
              </div>
            )}
          </div>
        </div>

        {/* Identity */}
        <div className="px-5 py-4">
          <p className="text-base font-bold text-text-primary">{currentServer?.name}</p>
          <p className="text-xs text-text-muted mt-0.5">{currentServer?.description || 'No description'}</p>
        </div>
      </div>

      {/* Edit form */}
      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
        <h3 className="text-sm font-bold text-text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
          <Pencil className="w-3.5 h-3.5 text-accent-color" /> Edit Server
        </h3>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-xs mb-4">
            <X className="w-3.5 h-3.5 flex-shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/30 text-success text-xs mb-4">
            <Check className="w-3.5 h-3.5 flex-shrink-0" /> {success}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <Field label="Server Name" htmlFor="server-name">
            <input
              id="server-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="settings-input"
              placeholder="Your server name"
              required
            />
          </Field>

          <Field label="Description" htmlFor="server-description">
            <textarea
              id="server-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="settings-input min-h-[80px] resize-y"
              placeholder="Describe your server..."
              maxLength={500}
              rows={3}
            />
            <p className="text-[10px] text-text-muted mt-1 text-right">{description.length}/500</p>
          </Field>

          <Field label="Server Icon" htmlFor="server-icon-upload">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-bg-tertiary border border-border-color overflow-hidden flex-shrink-0">
                {iconUrl ? (
                  <img src={iconUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-sm font-bold text-text-muted">
                    {currentServer?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <button
                type="button"
                id="server-icon-upload"
                onClick={() => iconFileRef.current?.click()}
                disabled={uploadingIcon}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-bg-tertiary border border-border-color text-xs font-medium text-text-secondary hover:text-text-primary hover:border-accent-color/50 transition-all disabled:opacity-50"
              >
                {uploadingIcon ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploadingIcon ? 'Uploading...' : 'Change Icon'}
              </button>
            </div>
          </Field>

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; htmlFor: string; children: React.ReactNode }> = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs font-semibold text-text-secondary mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

export default OverviewTab;
